interface StorageProviderOptions {
	query?: boolean,
	local?: boolean | 'session',
}

const storageProviderOptionsDefault: StorageProviderOptions = {
	query: true,
	local: true,
}

export class StorageProvider<T extends number | boolean | string | string[] | object> {
	private options: StorageProviderOptions;
	private firstLocalLoad = true;
	private firstQueryLoad = true;
	private cachedValue: T | undefined;

	constructor(private key: string, opts?: StorageProviderOptions) {
		this.options = { ...storageProviderOptionsDefault, ...opts };
	}

	public set value(value: T) {
		this.cachedValue = undefined;
		let storeValue: string = '';
		if (typeof value === "number") {
			storeValue = value.toString();
		} else if (typeof value === "boolean") {
			storeValue = value.toString();
		} else if (typeof value === "string") {
			storeValue = value;
		} else if (value instanceof Array) {
			storeValue = stringify(value);
		} else {
			try {
				storeValue = stringify(value);
			} catch {

			}
		}
		if (this.options.local) {
			window.localStorage.setItem(this.key, storeValue);
		}
		if (this.options.query) {
			this.updateLocalStorageQuery(storeValue, false);
		}
		this.firstLocalLoad = false;
		this.firstQueryLoad = false;
	}

	public get value(): T | undefined {
		if (this.cachedValue) return this.cachedValue;
		let result = undefined;
		let value: string | null = null;
		if (this.loadQuery) {
			value = this.getQueryParam();
		}
		if (!value && this.loadLocal) {
			value = window.localStorage.getItem(this.key);
		}
		if (this.loadQuery) {
			this.updateLocalStorageQuery(value, false)
		}
		if (value === null) return result;
		try {
			result = parse(value);
		}
		catch {
		}
		this.cachedValue = result ?? value;
		this.firstLocalLoad = false;
		this.firstQueryLoad = false;
		return this.cachedValue;
	}

	public delete() {
		window.localStorage.removeItem(this.key);
	}

	private get loadLocal(): boolean {
		return !(this.firstLocalLoad && this.options.local === 'session');
	}

	private get loadQuery(): boolean {
		return this.options.query! && this.firstQueryLoad;
	}

	private getQueryParam(): string | null {
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		return urlParams.get(this.key);
	}

	private updateLocalStorageQuery(value: string | null, updateUrl = true) {
		const queryString = window.localStorage.getItem('query');
		const queryMap: Map<string, string | null> = queryString ? parse(queryString) : new Map();
		if (queryMap.get(this.key) === value) return;


		queryMap.set(this.key, value);
		window.localStorage.setItem('query', stringify(queryMap));


		const url = new URL(window.location.href);
		if (!updateUrl) {
			// history.replaceState({}, '', url.pathname);
			return;
		}
		const params = url.searchParams;

		for (const query of queryMap) {
			if (!query[1] || query[1] === '[]') {
				params.delete(query[0]);
			} else {
				params.set(query[0], query[1]);
			}
		}

		const newUrl = url.pathname + '?' + params.toString() + url.hash;
		history.replaceState({}, '', newUrl);
	}

}

function stringify(value: any): string {
	return JSON.stringify(value, replacer);
}

function parse(text: string): any {
	return JSON.parse(text, reviver);
}

function replacer(_: string, value: any): any {
	if (value instanceof Map) {
		return {
			dataType: 'Map',
			value: Array.from(value.entries()),
		};
	} else {
		return value;
	}
}

function reviver(_: string, value: any): any {
	if (typeof value === 'object' && value !== null) {
		if (value.dataType === 'Map') {
			return new Map(value.value);
		}
	}
	return value;
}

export namespace StorageProvider {
	export function QueryMap(): Map<string, string | null> {
		const queryString = window.localStorage.getItem('query');
		const queryMap: Map<string, string | null> = queryString ? parse(queryString) : new Map();

		return queryMap;
	}

	export function QuerySearchParams(): URLSearchParams {
		const result = new URLSearchParams();
		const queryMap = QueryMap();
		for (const [key, value] of queryMap) {
			if (!key || !value || value === '[]') continue;
			result.set(key, value);
		}
		return result;
	}
}